/**
 * アクションボタン用アイコン
 *
 * atoms/Icon/*.svg を name 指定でインライン描画するラッパー。
 * SVG 内のハードコードされた色は currentColor に正規化され、
 * Tailwind の text-* で着色できる。
 *
 * 使用例:
 *   <ActionIcon name="add" className="w-4 h-4" />
 */
import type { FC } from "react";

const RAW_ACTION_SVGS = import.meta.glob("./*.svg", {
    eager: true,
    query: "?raw",
    import: "default",
}) as Record<string, string>;

/** SVG 内のハードコード色を currentColor に正規化 */
function normalizeSvgColors(svg: string): string {
    return svg
        .replace(/stroke="(?!none|currentColor|url\()[^"]*"/g, 'stroke="currentColor"')
        .replace(/fill="(?!none|currentColor|url\()[^"]*"/g, 'fill="currentColor"')
        .replace(/stroke:\s*(?!none|currentColor|url\()[^;"}\s]+/g, "stroke: currentColor")
        .replace(/fill:\s*(?!none|currentColor|url\()[^;"}\s]+/g, "fill: currentColor");
}

/** ファイル名（拡張子なし）→ 正規化済み SVG 文字列 */
const ICON_MAP: Record<string, string> = Object.fromEntries(
    Object.entries(RAW_ACTION_SVGS).map(([filePath, raw]) => {
        const filename = filePath.split("/").pop() ?? "";
        const nameWithoutExt = filename.replace(/\.svg$/, "");
        return [nameWithoutExt, normalizeSvgColors(raw)];
    }),
);

interface ActionIconProps {
    /** atoms/Icon/<name>.svg のファイル名（拡張子なし） */
    name: string;
    className?: string;
}

export const ActionIcon: FC<ActionIconProps> = ({ name, className }) => {
    const svg = ICON_MAP[name];
    if (!svg) return null;
    return (
        <span
            className={`inline-block [&>svg]:w-full [&>svg]:h-full ${className ?? ""}`}
            dangerouslySetInnerHTML={{ __html: svg }}
        />
    );
};
